/**
 * Homework service: event_homework and homework_submissions.
 * All DB access for homework goes through this module.
 */

import { supabase } from '../lib/supabase'
import {
  handleQuery,
  formatSupabaseError,
  accessDeniedResponse,
  resolveRoleContext,
  ensureCanManageEvent
} from './utils'

/**
 * List homework for an event (optionally by session). Students see only for registered events.
 * @param {string} eventId
 * @param {{ role?: string, userId?: string }} [options]
 * @returns {Promise<{ data: Array<any>, error: object | null }>}
 */
export const listEventHomework = async (eventId, options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { data: null, error: roleContext.error }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'student') {
    const { data, error } = await supabase
      .from('event_homework')
      .select('*, created_by_user:users!created_by(full_name)')
      .eq('event_id', eventId)
      .order('due_date', { ascending: true, nullsFirst: false })
    if (error) return { data: null, error: formatSupabaseError(error, 'Unable to load homework.') }
    return { data: data ?? [], error: null }
  }

  if (resolvedRole === 'agent') {
    const access = await ensureCanManageEvent(eventId)
    if (!access.allowed) return { data: null, error: access.error }
  }

  const { data, error } = await supabase
    .from('event_homework')
    .select('*, created_by_user:users!created_by(full_name)')
    .eq('event_id', eventId)
    .order('due_date', { ascending: true, nullsFirst: false })

  return handleQuery(
    Promise.resolve({ data, error }),
    { fallbackMessage: 'Unable to load homework.', allowEmpty: true }
  )
}

/**
 * List all homework relevant to a student (across their registered events).
 * @param {string} studentId
 * @param {{ role?: string, userId?: string }} [options]
 */
export const listStudentHomework = async (studentId, options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { data: null, error: roleContext.error }

  if (roleContext.role === 'student' && roleContext.userId !== studentId) {
    return accessDeniedResponse('You can only view your own homework.')
  }

  const { data: regs, error: regError } = await supabase
    .from('event_registrations')
    .select('event_id')
    .eq('student_id', studentId)

  if (regError || !regs?.length) {
    return { data: [], error: null }
  }

  const eventIds = regs.map((r) => r.event_id).filter(Boolean)
  const { data: homework, error: hwError } = await supabase
    .from('event_homework')
    .select('*, event:events(id, title)')
    .in('event_id', eventIds)
    .order('due_date', { ascending: true, nullsFirst: false })

  if (hwError) return { data: null, error: formatSupabaseError(hwError, 'Unable to load homework.') }

  const { data: submissions } = await supabase
    .from('homework_submissions')
    .select('*')
    .eq('student_id', studentId)

  const byHomework = (submissions ?? []).reduce((acc, s) => {
    acc[s.homework_id] = s
    return acc
  }, {})

  const withSubmission = (homework ?? []).map((h) => ({
    ...h,
    submission: byHomework[h.id] ?? null
  }))

  return { data: withSubmission, error: null }
}

/**
 * Create homework (agent/admin). Must be able to manage the event.
 */
export const createHomework = async (payload, options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { data: null, error: roleContext.error }

  if (roleContext.role === 'student') {
    return accessDeniedResponse('Students cannot create homework.')
  }

  if (roleContext.role === 'agent') {
    const access = await ensureCanManageEvent(payload.event_id)
    if (!access.allowed) return { data: null, error: access.error }
  }

  const row = {
    event_id: payload.event_id,
    session_id: payload.session_id ?? null,
    title: payload.title,
    description: payload.description ?? null,
    due_date: payload.due_date ?? null,
    created_by: roleContext.userId,
    updated_at: new Date().toISOString()
  }

  const { data, error } = await supabase
    .from('event_homework')
    .insert(row)
    .select('*')
    .single()

  if (error) return { data: null, error: formatSupabaseError(error, 'Unable to create homework.') }
  return { data, error: null }
}

/**
 * Student submits homework (or updates draft).
 */
export const submitHomework = async (homeworkId, studentId, submissionData, options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { data: null, error: roleContext.error }

  if (roleContext.role === 'student' && roleContext.userId !== studentId) {
    return accessDeniedResponse('You can only submit your own homework.')
  }

  const { submission_text, file_url } = submissionData ?? {}

  const { data: existing } = await supabase
    .from('homework_submissions')
    .select('id')
    .eq('homework_id', homeworkId)
    .eq('student_id', studentId)
    .maybeSingle()

  const payload = {
    submission_text: submission_text ?? null,
    file_url: file_url ?? null,
    submitted_at: new Date().toISOString(),
    status: 'submitted'
  }

  if (existing) {
    const { data, error } = await supabase
      .from('homework_submissions')
      .update(payload)
      .eq('id', existing.id)
      .select('*')
      .single()
    if (error) return { data: null, error: formatSupabaseError(error, 'Unable to update submission.') }
    return { data, error: null }
  }

  const { data, error } = await supabase
    .from('homework_submissions')
    .insert({
      homework_id: homeworkId,
      student_id: studentId,
      ...payload
    })
    .select('*')
    .single()

  if (error) return { data: null, error: formatSupabaseError(error, 'Unable to submit homework.') }
  return { data, error: null }
}

/**
 * Grade a submission (agent/instructor/admin).
 */
export const gradeHomework = async (submissionId, gradingData, options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { data: null, error: roleContext.error }

  if (roleContext.role === 'student') {
    return accessDeniedResponse('Only instructors or agents can grade homework.')
  }

  const { data: sub } = await supabase
    .from('homework_submissions')
    .select('homework_id')
    .eq('id', submissionId)
    .single()

  if (!sub) return { data: null, error: { message: 'Submission not found.', code: 'not_found' } }

  const { data: hw } = await supabase
    .from('event_homework')
    .select('event_id')
    .eq('id', sub.homework_id)
    .single()

  if (hw && roleContext.role === 'agent') {
    const access = await ensureCanManageEvent(hw.event_id)
    if (!access.allowed) return { data: null, error: access.error }
  }

  const payload = {
    grade_stars: gradingData.grade_stars ?? null,
    feedback_text: gradingData.feedback_text ?? null,
    graded_by: roleContext.userId,
    graded_at: new Date().toISOString(),
    status: 'graded'
  }

  const { data, error } = await supabase
    .from('homework_submissions')
    .update(payload)
    .eq('id', submissionId)
    .select('*')
    .single()

  if (error) return { data: null, error: formatSupabaseError(error, 'Unable to save grade.') }
  return { data, error: null }
}

/**
 * Get a single submission for a homework and student.
 */
export const getHomeworkSubmission = async (homeworkId, studentId, options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { data: null, error: roleContext.error }

  if (roleContext.role === 'student' && roleContext.userId !== studentId) {
    return accessDeniedResponse('You can only view your own submission.')
  }

  const { data, error } = await supabase
    .from('homework_submissions')
    .select('*')
    .eq('homework_id', homeworkId)
    .eq('student_id', studentId)
    .maybeSingle()

  return handleQuery(
    Promise.resolve({ data, error }),
    { fallbackMessage: 'Unable to load submission.', allowEmpty: true }
  )
}
